import React, { useState } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DATE_FIELDS = [
  { value: 'created_at', label: 'Booking Created Date' },
  { value: 'paid_at', label: 'Payment Paid Date' },
  { value: 'delivered_at', label: 'Delivery Date' },
];
const STATUSES = [
  'ALL', 'BOOKED', 'PAID', 'SCHEDULED', 'PICKED_UP',
  'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED', 'DELAYED_PICKUP',
];

/**
 * Admin → Settings card for downloading bookings as an Excel report with
 * date range + status filters. Talks to GET /api/admin/reports/bookings.xlsx
 * and streams the file directly to the browser download.
 */
export const ReportsExportCard = () => {
  // Default: this calendar month
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);

  const [dateField, setDateField] = useState('created_at');
  const [dateFrom, setDateFrom] = useState(iso(monthStart));
  const [dateTo, setDateTo] = useState(iso(today));
  const [status, setStatus] = useState('ALL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const params = new URLSearchParams({
        date_field: dateField,
        date_from: dateFrom,
        date_to: dateTo,
        status,
      });
      const res = await axios.get(`${API}/admin/reports/bookings.xlsx?${params}`, {
        responseType: 'blob',
      });
      // Extract filename from Content-Disposition
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const filename = m ? m[1] : `LFJ_Bookings_${dateFrom}_to_${dateTo}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      const msg = err?.response?.status === 401
        ? 'Session expired — please log in again.'
        : (err?.response?.data?.detail || err?.message || 'Download failed');
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const setPreset = (kind) => {
    const t = new Date();
    if (kind === 'this-month') {
      setDateFrom(iso(new Date(t.getFullYear(), t.getMonth(), 1)));
      setDateTo(iso(t));
    } else if (kind === 'last-month') {
      const start = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const end = new Date(t.getFullYear(), t.getMonth(), 0);
      setDateFrom(iso(start));
      setDateTo(iso(end));
    } else if (kind === 'last-30') {
      const s = new Date();
      s.setDate(s.getDate() - 30);
      setDateFrom(iso(s));
      setDateTo(iso(t));
    } else if (kind === 'ytd') {
      setDateFrom(iso(new Date(t.getFullYear(), 0, 1)));
      setDateTo(iso(t));
    }
  };

  return (
    <div
      data-testid="reports-export-card"
      className="rounded-2xl border border-[#1A1A1A]/10 bg-white p-6 shadow-sm"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#FFF1E8] flex items-center justify-center flex-shrink-0">
          <FileSpreadsheet size={20} className="text-[#FF5A1F]" />
        </div>
        <div>
          <h3 className="text-base font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Download Report (Excel)
          </h3>
          <p className="text-xs text-[#737373] mt-0.5">
            Bookings filtered by date range + status — downloads as a formatted .xlsx with a summary sheet.
          </p>
        </div>
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          ['this-month', 'This month'],
          ['last-month', 'Last month'],
          ['last-30', 'Last 30 days'],
          ['ytd', 'Year to date'],
        ].map(([k, lbl]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPreset(k)}
            data-testid={`report-preset-${k}`}
            className="px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide bg-[#FAF6EE] border border-[#1A1A1A]/10 text-[#1A1A1A] hover:bg-[#FFF1E8] hover:border-[#FF5A1F]/40 transition"
          >
            {lbl}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Date field</label>
          <select
            data-testid="report-date-field"
            value={dateField}
            onChange={(e) => setDateField(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] text-sm outline-none focus:border-[#FF5A1F] focus:bg-white transition"
          >
            {DATE_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Status</label>
          <select
            data-testid="report-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] text-sm outline-none focus:border-[#FF5A1F] focus:bg-white transition"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">From</label>
          <input
            data-testid="report-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] text-sm outline-none focus:border-[#FF5A1F] focus:bg-white transition"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">To</label>
          <input
            data-testid="report-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] text-sm outline-none focus:border-[#FF5A1F] focus:bg-white transition"
          />
        </div>
      </div>

      <button
        data-testid="report-download-btn"
        onClick={download}
        disabled={busy || !dateFrom || !dateTo}
        className="w-full sm:w-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] hover:shadow-[0_8px_24px_rgba(255,90,31,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        <Download size={16} /> {busy ? 'Building report…' : 'Download .xlsx'}
      </button>

      {error && (
        <div
          data-testid="report-error"
          className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-3"
        >
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ReportsExportCard;
